const MAX_VISIBLE_PHOTOS = 3;
const LIGHTBOX_PHOTO_WIDTH = 1600;

/*
 * Judge.me's own widget pages through a product's reviews via this endpoint
 * (see reviews_for_widget in its widget/main.js) - public, CORS-enabled, no
 * token, and keyed by product id rather than storefront URL, so it returns
 * the same full list on /ar as on English. per_page is capped at 30 server
 * side (verified live: larger values still return 30).
 */
const JDGM_API_HOST = 'https://cdn.judge.me';
const JDGM_PER_PAGE = 30;
// Load the next page once the shopper is within this many cards of the end
// of what's loaded - comfortably more than the 4 visible on desktop.
const JDGM_PREFETCH_AHEAD = 12;
const ANY_VISIBLE = /[^\s\p{P}]/u;

/**
 * Judge.me's review photo URLs carry a `width` resize parameter (their CDN
 * serves any requested size off the same original file). The thumbnail img's
 * data-src is a small ~160px crop meant for inline display; requesting a much
 * larger width directly from the same URL gives a real full-size image for
 * the lightbox, without depending on other DOM locations in the widget (whose
 * markup for "full size" links varies and isn't reliably present everywhere -
 * verified live that this width rewrite returns a real larger image).
 */
function getLightboxPhotoUrl(thumbSrc) {
  if (!thumbSrc) return thumbSrc;
  if (/([?&])width=\d+/.test(thumbSrc)) {
    return thumbSrc.replace(/([?&])width=\d+/, `$1width=${LIGHTBOX_PHOTO_WIDTH}`);
  }
  return thumbSrc;
}

/**
 * Judge.me's own rendered date text is locale-formatted by ITS OWN widget
 * language setting, not necessarily the storefront's - so it's read here only
 * as a fallback. The `datetime` attribute is a real ISO 8601 UTC timestamp,
 * which lets us format the date ourselves via Intl, matching the page's own
 * language (document.documentElement.lang) rather than Judge.me's.
 */
function formatReviewDate(timeEl) {
  if (!timeEl) return null;

  const iso = timeEl.getAttribute('datetime');
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return { iso: null, text: timeEl.textContent.trim() || null };
  }

  const text = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);

  return { iso, text };
}

// Parsed into a <template> so it stays inert - a detached <div> would still
// start downloading every <img src> in Judge.me's markup.
function parseReviewsHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  return [...template.content.querySelectorAll('.jdgm-rev')];
}

async function fetchReviewsPage({ productId, shopDomain }, page, languageParams) {
  const params = new URLSearchParams({
    ...languageParams,
    shop_domain: shopDomain,
    platform: 'shopify',
    product_id: productId,
    per_page: String(JDGM_PER_PAGE),
    page: String(page),
    // Hourly cache bucket, same idea as Judge.me's own `ts` param - keeps
    // their CDN from serving a list that's days old.
    ts: String(Math.floor(Date.now() / 3600000) * 3600),
  });
  const response = await fetch(`${JDGM_API_HOST}/reviews/reviews_for_widget?${params}`);
  if (!response.ok) throw new Error(`Judge.me reviews request failed: ${response.status}`);
  return response.json();
}

class JudgemeReviewSlider extends HTMLElement {
  connectedCallback() {
    this.sourceEl = this.querySelector('[data-jdgm-source]');
    this.widgetHost = this.sourceEl?.querySelector('.jdgm-widget') ?? null;

    this.slidesEl = this.querySelector('[data-jdgm-slides]');
    this.cardTemplate = this.querySelector('[data-jdgm-card-template]');
    this.starTemplate = this.querySelector('[data-jdgm-star-template]');
    this.photoTemplate = this.querySelector('[data-jdgm-photo-template]');
    this.photoOverlayTemplate = this.querySelector('[data-jdgm-photo-overlay-template]');

    this.lightboxEl = this.querySelector('[data-jdgm-lightbox]');
    this.lightboxSlidesEl = this.querySelector('[data-jdgm-lightbox-slides]');
    this.lightboxSlideTemplate = this.querySelector('[data-jdgm-lightbox-slide-template]');
    this.lightboxSwiper = null;
    this.lightboxTriggerEl = null;

    this.thankyouEl = this.querySelector('[data-jdgm-thankyou]');

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSubmissionMutation = this.handleSubmissionMutation.bind(this);
    this.addEventListener('click', this.handleClick);

    this.apiSource = this.dataset.productId && this.dataset.shopDomain
      ? { productId: this.dataset.productId, shopDomain: this.dataset.shopDomain }
      : null;

    if (!this.widgetHost && !this.apiSource) return;

    this.hasRendered = false;
    this.seenReviewIds = new Set();
    this.pendingReviews = 0;
    this.handleWidgetMutation = this.handleWidgetMutation.bind(this);

    // This section is typically well below the fold on a product page, so
    // nothing is fetched until it's close to entering the viewport.
    if ('IntersectionObserver' in window) {
      this.lazyLoadObserver = new IntersectionObserver(this.handleLazyLoadIntersect.bind(this), {
        rootMargin: '400px 0px',
      });
      this.lazyLoadObserver.observe(this);
    } else {
      this.startHydration();
    }
  }

  disconnectedCallback() {
    this.lazyLoadObserver?.disconnect();
    this.stopObserving();
    this.stopSubmissionWatch();
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleLazyLoadIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.lazyLoadObserver?.disconnect();
    this.lazyLoadObserver = null;
    this.startHydration();
  }

  startHydration() {
    if (this.apiSource) {
      this.loadFromApi();
      return;
    }
    this.startWidgetHydration();
  }

  /*
   * Reviews come in Judge.me's "Show reviews in the customer's language
   * first" order: everything it files under the page's language
   * (primary_language), then the rest (exclude_language) - the same two
   * requests its own widget/main.js makes, each keeping Judge.me's sort.
   * Page 1 of both is fetched up front (that also gives the real total for
   * the counter); later pages load as the shopper nears the end of what's
   * rendered (loadMoreIfNeeded), so a product with 500+ reviews costs two
   * requests and ~30 cards until someone actually browses them.
   */
  async loadFromApi() {
    const language = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    const bucketParams = language
      ? [{ primary_language: language }, { exclude_language: language }]
      : [{}];

    let firstBatch;
    try {
      const firstPages = await Promise.all(
        bucketParams.map((params) => fetchReviewsPage(this.apiSource, 1, params))
      );
      this.buckets = bucketParams.map((params, index) => ({
        params,
        nextPage: 2,
        totalPages: Math.ceil((firstPages[index].total_count || 0) / JDGM_PER_PAGE),
        firstPage: parseReviewsHtml(firstPages[index].html),
      }));
      this.pendingReviews = firstPages.reduce((sum, page) => sum + (page.total_count || 0), 0);
      firstBatch = await this.fetchNextBatch();
    } catch (error) {
      // Fall back to whatever the in-page Judge.me widget holds (first page
      // only, and stale on /ar) rather than leaving the skeleton up.
      console.error(error);
      this.buckets = null;
      this.pendingReviews = 0;
      if (this.widgetHost) this.startWidgetHydration();
      else this.renderReviews([]);
      return;
    }

    if (this.hasRendered) return;
    this.renderReviews(firstBatch ?? []);
    this.loadMoreIfNeeded();
  }

  /** The next page of reviews in order, or null once every bucket is exhausted. */
  async fetchNextBatch() {
    for (const bucket of this.buckets) {
      if (bucket.firstPage) {
        const reviews = bucket.firstPage;
        bucket.firstPage = null;
        if (reviews.length) return this.consumeBatch(reviews);
      }
      if (bucket.nextPage <= bucket.totalPages) {
        // eslint-disable-next-line no-await-in-loop
        const page = await fetchReviewsPage(this.apiSource, bucket.nextPage, bucket.params);
        bucket.nextPage += 1;
        return this.consumeBatch(parseReviewsHtml(page.html));
      }
    }
    this.pendingReviews = 0;
    return null;
  }

  consumeBatch(reviews) {
    this.pendingReviews = Math.max(0, this.pendingReviews - reviews.length);
    return reviews;
  }

  async loadMoreIfNeeded() {
    if (this.isLoadingMore || !this.buckets || !this.carousel) return;

    const loadedCount = this.carousel.cells.length;
    if (loadedCount - this.carousel.selectedIndex > JDGM_PREFETCH_AHEAD) return;

    this.isLoadingMore = true;
    let batch = null;
    try {
      batch = await this.fetchNextBatch();
    } catch (error) {
      console.error(error);
      this.pendingReviews = 0;
    }
    this.isLoadingMore = false;

    if (!batch) this.buckets = null;
    this.carousel.appendCells(batch ? this.buildCards(batch) : []);
    if (batch) this.loadMoreIfNeeded();
  }

  startWidgetHydration() {
    this.observer = new MutationObserver(this.handleWidgetMutation);
    this.observer.observe(this.widgetHost, {
      attributes: true,
      attributeFilter: ['class', 'data-src'],
      childList: true,
      subtree: true,
    });

    this.maxWaitTimeoutId = window.setTimeout(() => this.finalizeRender({ force: true }), 8000);

    this.scheduleSettleCheck();
  }

  stopObserving() {
    this.observer?.disconnect();
    window.clearTimeout(this.maxWaitTimeoutId);
    window.clearTimeout(this.settleTimeoutId);
  }

  handleWidgetMutation() {
    this.scheduleSettleCheck();
  }

  scheduleSettleCheck() {
    if (this.hasRendered) return;

    window.clearTimeout(this.settleTimeoutId);
    this.settleTimeoutId = window.setTimeout(() => this.finalizeRender(), 350);
  }

  finalizeRender({ force = false } = {}) {
    if (this.hasRendered) return;

    const isDone = this.widgetHost.classList.contains('jdgm--done-setup-widget');
    if (!isDone && !force) return;

    // On some locales (observed on this store's Arabic widget) Judge.me marks
    // the widget "done" and goes quiet on mutations before it has actually
    // finished injecting customer photo <img> tags - they arrive a few
    // seconds later in a separate update. Treat any present-but-still-empty
    // photo <img> as "not settled yet" so we keep waiting (the settle-check/
    // max-wait timers already in place handle the rest) instead of locking
    // in a snapshot with no photos. Checked directly on .jdgm-rev__pic-img
    // rather than through a wrapper class, since Judge.me wraps photos in
    // .jdgm-rev__media for some locales and .jdgm-rev__pics for others
    // (observed on the Arabic widget).
    const hasPendingMedia = !force
      && [...this.widgetHost.querySelectorAll('.jdgm-rev__pic-img')].some((img) => !img.dataset.src);
    if (hasPendingMedia) return;

    this.renderReviews([...this.widgetHost.querySelectorAll('.jdgm-rev')]);
  }

  /** Card elements for reviews not already shown (pages can overlap). */
  buildCards(reviewEls) {
    return reviewEls
      .filter((reviewEl) => {
        const reviewId = reviewEl.dataset.reviewId;
        if (!reviewId || this.seenReviewIds.has(reviewId)) return false;
        this.seenReviewIds.add(reviewId);
        return true;
      })
      .map((reviewEl) => this.renderCard(reviewEl));
  }

  renderReviews(reviewEls) {
    this.hasRendered = true;
    this.stopObserving();

    const cards = this.buildCards(reviewEls);
    if (!cards.length) {
      this.classList.add('is-empty');
      return;
    }
    this.slidesEl.append(...cards);

    // is-ready must land before initSlider(): .judgeme-review-slider__viewport
    // is display:none until then, and Flickity measures cell widths off the
    // live DOM at construction time - building it while the viewport is still
    // hidden makes every cell measure as 0-width, so they all render stacked
    // on top of each other (verified live) instead of laid out side by side.
    this.classList.add('is-ready');
    this.initSlider();
  }

  appendStars(container, score) {
    if (!container || !this.starTemplate) return;

    container.replaceChildren();
    container.setAttribute('aria-label', (this.dataset.labelRated || '').replace('[score]', score));

    for (let index = 1; index <= 5; index += 1) {
      const star = this.starTemplate.content.cloneNode(true).firstElementChild;
      star.classList.add(index <= score ? 'is-full' : 'is-empty');
      container.append(star);
    }
  }

  renderCard(reviewEl) {
    // Rating-only reviews are still shown (as stars, author and date); a body
    // that's just punctuation - this store has many reviews whose text is "." -
    // is treated as no text rather than printed.
    const rawBody = reviewEl.querySelector('.jdgm-rev__body')?.textContent.trim() ?? '';
    const body = ANY_VISIBLE.test(rawBody) ? rawBody : '';

    const score = Number.parseInt(reviewEl.querySelector('.jdgm-rev__rating')?.dataset.score ?? '5', 10);
    const title = reviewEl.querySelector('.jdgm-rev__title')?.textContent.trim();
    const author = reviewEl.querySelector('.jdgm-rev__author')?.textContent.trim();
    const reviewDate = formatReviewDate(reviewEl.querySelector('.jdgm-rev__timestamp'));
    // Not scoped through a wrapper class - Judge.me wraps photos in
    // .jdgm-rev__media for some locales and .jdgm-rev__pics for others
    // (observed on the Arabic widget), but .jdgm-rev__pic-img itself is
    // consistent across both. data-src on the in-page widget (lazy-loaded);
    // the API's HTML may carry a plain src instead.
    const photos = [...reviewEl.querySelectorAll('.jdgm-rev__pic-img')]
      .map((img) => {
        const src = img.dataset.src || img.getAttribute('src');
        return { thumbSrc: src, fullSrc: getLightboxPhotoUrl(src), alt: img.getAttribute('alt') || '' };
      })
      .filter((photo) => photo.thumbSrc);

    const card = this.cardTemplate.content.cloneNode(true);
    const starsContainer = card.querySelector('.judgeme-review-slider__card-stars');
    const textEl = card.querySelector('.judgeme-review-slider__card-text');
    const photosEl = card.querySelector('.judgeme-review-slider__card-photos');
    const authorEl = card.querySelector('.judgeme-review-slider__card-author');
    const dateEl = card.querySelector('.judgeme-review-slider__card-date');

    this.appendStars(starsContainer, score);
    this.renderPhotos(photosEl, photos);

    if (title) {
      const titleEl = document.createElement('strong');
      titleEl.className = 'judgeme-review-slider__card-title';
      titleEl.textContent = title;
      textEl.append(titleEl);
    }
    if (body) {
      // Own element so CSS can clamp just the body (line-clamp doesn't apply
      // across the block-level title); the full text stays in the title attr.
      const bodyEl = document.createElement('span');
      bodyEl.className = 'judgeme-review-slider__card-body';
      bodyEl.textContent = body;
      bodyEl.title = body;
      textEl.append(bodyEl);
    }
    if (!title && !body) textEl.remove();

    if (author) authorEl.textContent = `— ${author}`;
    if (reviewDate?.text && dateEl) {
      dateEl.textContent = reviewDate.text;
      if (reviewDate.iso) dateEl.setAttribute('datetime', reviewDate.iso);
    }

    return card.firstElementChild;
  }

  renderPhotos(photosEl, photos) {
    if (!photos.length || !this.photoTemplate) return;

    const visiblePhotos = photos.slice(0, MAX_VISIBLE_PHOTOS);
    const remaining = photos.length - visiblePhotos.length;

    visiblePhotos.forEach((photo, index) => {
      const photoFrag = this.photoTemplate.content.cloneNode(true);
      const button = photoFrag.querySelector('.judgeme-review-slider__photo');
      const img = photoFrag.querySelector('.judgeme-review-slider__photo-img');

      img.src = photo.thumbSrc;
      img.alt = photo.alt;
      button.dataset.photoIndex = String(index);
      button.setAttribute(
        'aria-label',
        (this.dataset.labelPhoto || '').replace('[index]', index + 1).replace('[count]', photos.length)
      );

      const isLastVisible = index === visiblePhotos.length - 1;
      if (isLastVisible && remaining > 0 && this.photoOverlayTemplate) {
        const overlayFrag = this.photoOverlayTemplate.content.cloneNode(true);
        overlayFrag.querySelector('.judgeme-review-slider__photo-overlay').textContent = `+${remaining}`;
        button.append(overlayFrag);
      }

      photosEl.append(button);
    });

    photosEl.jdgmPhotos = photos;
  }

  handleClick(event) {
    const photoButton = event.target.closest('.judgeme-review-slider__photo');
    if (photoButton) {
      const photosEl = photoButton.closest('.judgeme-review-slider__card-photos');
      const photos = photosEl?.jdgmPhotos;
      const startIndex = Number.parseInt(photoButton.dataset.photoIndex ?? '0', 10);
      if (photos?.length) this.openLightbox(photos, startIndex, photoButton);
      return;
    }

    if (event.target.closest('[data-jdgm-lightbox-close]')) {
      this.closeLightbox();
      return;
    }

    if (event.target.closest('[data-jdgm-write-review-trigger]')) {
      this.openReviewForm();
      return;
    }

    if (event.target.closest('[data-jdgm-form-close]')) {
      this.closeReviewForm();
      return;
    }

    const cancelLink = event.target.closest('.jdgm-cancel-rev');
    if (cancelLink && this.sourceEl?.contains(cancelLink)) {
      event.preventDefault();
      this.closeReviewForm();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.sourceEl?.classList.contains('is-form-open')) {
      this.closeReviewForm();
      return;
    }

    this.closeLightbox();
  }

  openReviewForm() {
    if (!this.sourceEl || !this.widgetHost) return;

    const writeLink = this.widgetHost.querySelector('.jdgm-write-rev-link');
    if (!writeLink) return;

    const isExpanded = () => writeLink.getAttribute('aria-expanded') === 'true';
    const reveal = () => {
      this.sourceEl.classList.add('is-form-open');
      document.documentElement.classList.add('jdgm-lightbox-open');
      document.addEventListener('keydown', this.handleKeydown);
      this.startSubmissionWatch();
    };

    if (isExpanded()) {
      reveal();
      return;
    }

    writeLink.click();
    window.setTimeout(() => {
      if (!isExpanded()) writeLink.click();
      reveal();
    }, 200);
  }

  closeReviewForm() {
    this.stopSubmissionWatch();
    this.sourceEl?.classList.remove('is-thankyou');

    if (!this.sourceEl?.classList.contains('is-form-open')) return;

    this.sourceEl.classList.remove('is-form-open');
    document.documentElement.classList.remove('jdgm-lightbox-open');
    document.removeEventListener('keydown', this.handleKeydown);
  }

  startSubmissionWatch() {
    this.stopSubmissionWatch();
    this.submissionObserver = new MutationObserver(this.handleSubmissionMutation);
    this.submissionObserver.observe(document.body, { childList: true, subtree: true });
  }

  stopSubmissionWatch() {
    this.submissionObserver?.disconnect();
    this.submissionObserver = null;
  }

  handleSubmissionMutation(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (this.thankyouEl?.contains(node)) continue;

        const text = node.textContent || '';
        if (/review will be published|review submitted/i.test(text)) {
          this.handleReviewSubmitted(node);
          return;
        }
      }
    }
  }

  handleReviewSubmitted(bannerEl) {
    bannerEl.classList.add('jdgm-external-banner--hidden');
    this.stopSubmissionWatch();
    this.sourceEl?.classList.add('is-thankyou');
  }

  openLightbox(photos, startIndex, triggerEl) {
    if (!this.lightboxEl || !this.lightboxSlidesEl || !this.lightboxSlideTemplate) return;

    this.lightboxTriggerEl = triggerEl ?? null;

    // The carousel's cell list is fixed at build time, so reopening the
    // lightbox with a different photo set needs a fresh instance rather than
    // an incremental update.
    this.lightboxSwiper?.destroy();
    this.lightboxSwiper = null;

    this.lightboxSlidesEl.replaceChildren();

    photos.forEach((photo) => {
      const slideFrag = this.lightboxSlideTemplate.content.cloneNode(true);
      const img = slideFrag.querySelector('.judgeme-review-slider__lightbox-img');
      img.src = photo.fullSrc;
      img.alt = photo.alt;
      this.lightboxSlidesEl.append(slideFrag);
    });

    // One photo: no arrows or "1 / 1" counter (see .is-single in the CSS).
    this.lightboxEl.classList.toggle('is-single', photos.length < 2);
    this.lightboxEl.hidden = false;
    document.documentElement.classList.add('jdgm-lightbox-open');
    document.addEventListener('keydown', this.handleKeydown);

    if (typeof window.themeCarousel === 'function') {
      const lightboxViewport = this.querySelector('.judgeme-review-slider__lightbox-viewport');
      // themeCarousel binds click handlers to the nav buttons on every build;
      // these buttons outlive each lightbox instance, so swap in clean copies
      // or every reopen would stack another handler (one click = many slides).
      ['.judgeme-review-slider__lightbox-prev', '.judgeme-review-slider__lightbox-next'].forEach((selector) => {
        const button = this.querySelector(selector);
        button?.replaceWith(button.cloneNode(true));
      });
      this.lightboxSwiper = window.themeCarousel(lightboxViewport, {
        // Slides have a fixed CSS height; equalising would lock them to the
        // tallest photo's natural height instead.
        equalHeight: false,
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: startIndex,
        pagination: {
          el: this.querySelector('.judgeme-review-slider__lightbox-pagination'),
          type: 'fraction',
        },
        navigation: {
          nextEl: this.querySelector('.judgeme-review-slider__lightbox-next'),
          prevEl: this.querySelector('.judgeme-review-slider__lightbox-prev'),
        },
      });
    }

    this.querySelector('.judgeme-review-slider__lightbox-close')?.focus();
  }

  closeLightbox() {
    if (!this.lightboxEl || this.lightboxEl.hidden) return;

    this.lightboxEl.hidden = true;
    document.documentElement.classList.remove('jdgm-lightbox-open');
    document.removeEventListener('keydown', this.handleKeydown);
    this.lightboxTriggerEl?.focus();
    this.lightboxTriggerEl = null;
  }

  initSlider() {
    if (typeof window.themeCarousel !== 'function') return;

    const viewport = this.querySelector('.judgeme-review-slider__viewport');
    if (!viewport) return;

    this.carousel = window.themeCarousel(viewport, {
      pendingCells: () => this.pendingReviews,
      // Arabic pages inherit direction: rtl (theme.css body.lang-ar) - start
      // from the right so the first (customer's-language) review is read first.
      rightToLeft: getComputedStyle(this).direction === 'rtl',
      slidesPerView: 1.15,
      slidesPerGroup: 1,
      spaceBetween: 12,
      loop: false,
      allowTouchMove: true,
      pagination: {
        el: this.querySelector('.judgeme-review-slider__pagination'),
        type: 'fraction',
      },
      navigation: {
        nextEl: this.querySelector('.judgeme-review-slider__next'),
        prevEl: this.querySelector('.judgeme-review-slider__prev'),
      },
      breakpoints: {
        475: {
          slidesPerView: 1.3,
          spaceBetween: 12,
        },
        768: {
          slidesPerView: 1.8,
          spaceBetween: 16,
        },
        // 4-up only from 1440px: at 1024-1280 a quarter of the row is too
        // narrow for the stars + date head (dates overflowed - verified at 1024
        // and 1280 in headless screenshots).
        1024: {
          slidesPerView: 3,
          slidesPerGroup: 1,
          spaceBetween: 16,
        },
        1440: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 16,
        },
      },
    });

    this.carousel?.on('select', () => this.loadMoreIfNeeded());
  }
}

if (!customElements.get('judgeme-review-slider')) {
  customElements.define('judgeme-review-slider', JudgemeReviewSlider);
}
